/**
 * The product's text vocabulary — one component per type role.
 *
 * Every role bundles size, weight, leading and tracking in a single DS token
 * (`text-nf-ds-<role>`), and each component here is a thin wrapper over one of
 * them. There is deliberately no way to express a size that is not a role:
 * that closure is what keeps the app from drifting back to the 39 arbitrary
 * font sizes it accumulated when the size was a per-call-site decision.
 *
 * Components rather than raw classes because a class name is not type-checked.
 * `text-nf-ds-titel` compiles to nothing and renders silently unstyled;
 * `<Titel>` does not compile at all.
 *
 * These add nothing of their own beyond the role. If one of them starts
 * carrying a hardcoded size, the problem has moved inside the component rather
 * than been solved, and one-off sites will stop matching it again.
 *
 * @example
 * <Display>September static batch</Display>
 * <SectionTitle id="findings">Findings</SectionTitle>
 * <Body muted>From the latest account summary</Body>
 * <Rail>Purchase CPA</Rail>
 * <Figure>$48,209</Figure>
 *
 * Spec: packages/nf-design-system/colors_and_type.css (Type ROLES)
 */
import type { ElementType, ReactNode } from "react";
import { cn } from "@/lib/utils";

interface TextProps {
  children: ReactNode;
  className?: string;
  /** Override the rendered tag without changing its visual level. */
  as?: ElementType;
  /** De-emphasise. Colour, never a smaller size — that is the DS rule. */
  muted?: boolean;
  /**
   * Use the contrast-safe muted role for the cream page background.
   * `text-muted-foreground` is tuned for white and fails AA on paper.
   */
  onPaper?: boolean;
  id?: string;
  title?: string;
}

const mutedTone = (muted?: boolean, onPaper?: boolean): string | undefined => {
  if (!muted) return undefined;
  return onPaper ? "text-nf-ds-fg-muted-on-paper" : "text-muted-foreground";
};

/** Digits line up in columns everywhere; there is no reason to opt in per site. */
const NUMERIC = "[font-variant-numeric:tabular-nums]";

/**
 * Role → the complete utility name.
 *
 * Written out in full, never built from a template. Tailwind scans source text
 * for whole class names, so `text-nf-ds-${role}` is invisible to it and the
 * utility is never emitted — the component then renders with no size, weight,
 * leading or tracking and simply inherits. It compiles, it type-checks, and it
 * is wrong on screen, which is exactly how `text-nf-ds-section` shipped
 * missing from the build.
 */
const ROLE_CLASS = {
  display: "text-nf-ds-display",
  section: "text-nf-ds-section",
  "card-title": "text-nf-ds-card-title",
  title: "text-nf-ds-title",
  body: "text-nf-ds-body",
  hint: "text-nf-ds-hint",
  lede: "text-nf-ds-lede",
  label: "text-nf-ds-label",
  strong: "text-nf-ds-strong",
  rail: "text-nf-ds-rail",
  figure: "text-nf-ds-figure",
} as const;

type Role = keyof typeof ROLE_CLASS;

function make(role: Role, defaultTag: ElementType, extra?: string) {
  return function Text({
    children,
    className,
    as: As = defaultTag,
    muted,
    onPaper,
    id,
    title,
  }: TextProps) {
    return (
      <As
        id={id}
        title={title}
        className={cn(
          "font-nf-ds-sans",
          `text-nf-ds-${role}`,
          NUMERIC,
          extra,
          mutedTone(muted, onPaper),
          className,
        )}
      >
        {children}
      </As>
    );
  };
}

/** The page title. Once per page — a second one means it is really two pages. */
export const Display = make("display", "h1", "text-pretty");

/**
 * Section headings, between Display and Title.
 *
 * Keeps the explicit `text-nf-ds-fg` its previous implementation set. Dropping
 * it would let the heading inherit a muted colour from any parent that sets
 * one, which is a live change for 46 importers — the role swap is meant to be
 * a visual no-op.
 */
export const SectionTitle = make("section", "h2", "text-pretty text-nf-ds-fg");

/**
 * A card's own title, where the card is the object rather than a row.
 *
 * `section` sizing with `title` weight — 20/700 — because the two existing
 * roles each had half of it. `section` is 20/600, which is the heading *above*
 * a group of cards; `title` is 15/700, which is an item inside one. A screen
 * of cards that are each their own thing wants the section's presence with the
 * weight the guidelines reserve for a card, and with no role saying so five
 * call sites on one page had each hand-rolled a different pair: body plus 500,
 * body plus 600, and the section role.
 *
 * Use `Title` for a row inside a card, and this for the card.
 */
export const CardTitle = make("card-title", "h3", "text-pretty");

/** Card and panel headings — anything with a body under it. */
export const Title = make("title", "h3", "text-pretty");

/** Prose and table cells. The default. */
export const Body = make("body", "p");

/** Emphasis at body size — the one column in a table that carries the answer. */
export const Strong = make("strong", "span");

/**
 * The line under a field, and any supporting line beside a name.
 *
 * The same 13px as a label on purpose: one size, two roles, separated by
 * weight and colour. De-emphasis is colour, never a smaller size — a hint set
 * smaller than its label reads as fine print rather than guidance.
 */
export const Hint = make("hint", "span");

/**
 * The standfirst under a page title — one sentence saying what the screen is
 * for, or what is waiting on you.
 *
 * Bigger than body on purpose, and muted. The hierarchy against the title
 * above it is carried by colour, which is the house rule: a subtitle set small
 * enough to be unmistakably secondary is a subtitle nobody reads. Pair it with
 * `Display`; `Subtitle` is the much quieter line that sits under a section.
 *
 * Measured, because 17px with no cap is a sentence running the full width of a
 * 1400-point page. Roughly 65 characters, which is where a line stops being
 * comfortable to track back from.
 */
export const Lede = make("lede", "p", "max-w-prose text-pretty");

/**
 * A field's label, and the line naming a figure.
 *
 * The same 13px as `Hint`, separated from it by weight alone — which is the
 * house rule rather than an accident: de-emphasis is colour and weight, never
 * a smaller size.
 */
export const Label = make("label", "span");

/**
 * Mono at reading size — a chip, a counter, a short piece of metadata.
 *
 * Not `Rail`: that one is the uppercase structural label at 10px, for the
 * edges of a layout. This sits inside content at 11px and keeps its case,
 * because a chip reading "#1" or a payout has nothing to upcase.
 *
 * A function rather than `make` for the same reason `Rail` is one — `make`
 * fixes the sans face, and these two are the mono roles.
 */
export function Caption({
  children,
  className,
  as: As = "span",
  muted,
  onPaper,
  id,
  title,
}: TextProps) {
  return (
    <As
      id={id}
      title={title}
      className={cn(
        "font-nf-ds-mono text-nf-ds-caption",
        NUMERIC,
        mutedTone(muted, onPaper),
        className,
      )}
    >
      {children}
    </As>
  );
}

/**
 * The structural rail: table heads, eyebrows, KPI labels. Mono, and uppercase
 * is applied here rather than left to the caller — every use of this role in
 * the product is uppercase, and leaving it optional invites two variants.
 */
export function Rail({
  children,
  className,
  as: As = "span",
  muted = true,
  onPaper,
  id,
  title,
}: TextProps) {
  return (
    <As
      id={id}
      title={title}
      className={cn(
        "font-nf-ds-mono text-nf-ds-rail uppercase",
        mutedTone(muted, onPaper),
        className,
      )}
    >
      {children}
    </As>
  );
}

/** A single number that is the point — KPI values. */
export const Figure = make("figure", "span", "tabular-nums");

/**
 * Supporting copy under a section heading.
 *
 * @deprecated Prefer `<Body muted>`. This keeps its original 12.5px/1.45 so
 * that adopting the roles is not a silent resize across its 46 importers;
 * migrating it is a deliberate visual change, not a refactor.
 */
export function Subtitle({
  children,
  className,
  as: As = "p",
  onPaper,
  id,
  title,
}: TextProps) {
  return (
    <As
      id={id}
      title={title}
      className={cn(
        "font-nf-ds-sans text-[12.5px] font-[400] leading-[1.45] max-w-[62ch] text-pretty",
        NUMERIC,
        mutedTone(true, onPaper),
        className,
      )}
    >
      {children}
    </As>
  );
}
